import React from 'react';
import PropTypes from 'prop-types';
import * as R from 'ramda';
import get from 'lodash/get';
import isEqual from 'lodash/isEqual';
import { Helmet } from 'react-helmet';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';

import Button from '@mui/material/Button';
import { Field, reduxForm } from 'redux-form';
import { connect } from 'react-redux';

import { withStyles } from '@mui/styles';
import { withTranslation } from 'react-i18next';

import SelectedServiceBox from '../subcomponents/SelectedServiceBox';
import Breadcrumb from '../subcomponents/Breadcrumb';
import SSNDialog from '../subcomponents/SSNDialog';
import Title from '../subcomponents/Title';

import { createReservation } from '../../store/actions/appointmentActions';

import { validate, returnField } from '../../utils/FormHelpers';

import { getPageTitle } from '../../utils/SEO';

import { siteIds } from '../../constants';
import { settings, siteId } from '../../sites';

const inlineStyles = theme => ({
  button: {
    width: '100%',
    fontSize: 14,
    height: 50,
    [theme.breakpoints.up('md')]: {
      height: 58,
    },
  },
  expansionPanel: {
    width: '100%',
  },
  formGrid: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    paddingBottom: theme.spacing(4),
    order: 2,
  },
  paddingLeftRight: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    order: 1,
  },
  content: {
    marginTop: 20,
  },
  note: {
    color: 'rgb(86,99,110)',
    marginTop: 20,
  },
  selectedServiceBoxWrapper: {
    order: 2,
    [theme.breakpoints.up('md')]: {
      order: 3,
    },
  },
});

const required = value => (value ? undefined : 'required');

const ssncheck = value => {
  const ssnRegExp = /^(0[1-9]|[12]\d|3[01])(0[1-9]|1[0-2])(\d\d-|[01]\dA|2[0-4]A)\d{3}[\dABCDEFHJKLMNPRSTUVWXY]$/g;
  const gChecks = '0123456789ABCDEFHJKLMNPRSTUVWXY';
  const ssnCheck = ssnRegExp.exec(value);

  if (ssnCheck) {
    const tmp1 = gChecks.charAt(parseInt(value.substr(0, 6) + value.substr(7, 3), 10) % 31);
    return tmp1 === value.charAt(10) ? undefined : 'ssnrequired';
  }
  return Array.isArray(ssnCheck) && ssnCheck.length > 0 ? undefined : 'ssnrequired';
};

const birthdatecheck = value => {
  const regExp = /^(0[1-9]|[12]\d|3[01])[.](0[1-9]|1[0-2])[.](19|20)\d{2}$/g;
  const checkResult = regExp.exec(value);
  return Array.isArray(checkResult) && checkResult.length > 0 ? undefined : 'dateofbirthrequired';
};

/**
 * ConfirmReservation
 * User fills in information and on submit the service will confirm reservation
 * @param {Object} classes    Classes from JSS inlineStyles
 */

const ConfirmReservation = ({
  classes,
  handleSubmit,
  // eslint-disable-next-line no-shadow
  createReservation,
  submitting,
  selectedSubService,
  t,
  lang,
  subServiceUrlFragment,
  brandSettings,
  localeLong,
}) => {
  const contentBlock = R.path(['ssnInfoContent', 'fields'], brandSettings);

  let phoneString = '';

  // eslint-disable-next-line no-plusplus
  for (let i = 1; i <= get(settings, ['phone', 'numberOfDigits']); i++) {
    phoneString += i;
  }

  const phoneNumberExample = phoneString;

  return (
    <Grid container direction="row" spacing={0}>
      <Grid item xs={12} className={classes.breadcrumb}>
        <Breadcrumb lang={lang} currenttabindex={3} subServiceUrlFragment={subServiceUrlFragment} />
      </Grid>
      <Helmet>
        <meta charSet="utf-8" />
        <title>{getPageTitle(t('views.confirmReservation.userDetails'))}</title>
      </Helmet>
      <Grid item xs={12} className={classes.paddingLeftRight}>
        <Title icon="list">{t('views.confirmReservation.title')}</Title>
      </Grid>
      <Grid item xs={12} md={4} className={classes.selectedServiceBoxWrapper}>
        <SelectedServiceBox content={selectedSubService} />
      </Grid>
      <Grid item xs={12} md={8} className={classes.formGrid}>
        <Typography variant="h3">{t('views.confirmReservation.userDetails')}</Typography>
        <form className={classes.content} onSubmit={handleSubmit(createReservation)}>
          <Grid container>
            <Grid item xs={12} md={7}>
              <Field
                key="firstName"
                id="firstName"
                name="firstName"
                component={returnField('text')}
                label={t('views.confirmReservation.firstName')}
                type="text"
                fullWidth
                validate={[required]}
                required
                autoComplete="given-name"
              />
            </Grid>
          </Grid>
          <Grid container>
            <Grid item xs={12} md={7}>
              <Field
                key="lastName"
                id="lastName"
                name="lastName"
                component={returnField('text')}
                label={t('views.confirmReservation.lastName')}
                type="text"
                fullWidth
                validate={[required]}
                required
                autoComplete="family-name"
              />
            </Grid>
          </Grid>

          {isEqual(siteId, siteIds.InstrumentariumEE) ? (
            <Grid container>
              <Grid item xs={12} md={7}>
                <Field
                  key="dateOfBirth"
                  id="dateOfBirth"
                  name="dateOfBirth"
                  component={returnField('text')}
                  label={t('views.confirmReservation.dateOfBirth.label')}
                  type="text"
                  fullWidth
                  validate={[birthdatecheck]}
                  placeholder={t('views.confirmReservation.dateOfBirth.placeholder')}
                  required
                />
              </Grid>
            </Grid>
          ) : (
            <Grid container>
              <Grid item xs={12} md={7}>
                <Field
                  key="personalID"
                  id="personalID"
                  name="personalID"
                  component={returnField('text')}
                  label={t('views.confirmReservation.personalID')}
                  type="text"
                  fullWidth
                  validate={[ssncheck]}
                  placeholder="PPKKVV-XXXX"
                  noBottomMargin
                  required
                />
                <Box mb={2}>
                  <SSNDialog
                    content={R.path([`contentIn${localeLong}`], contentBlock)}
                    helpText={t('views.confirmReservation.ssnHelp')}
                  />
                </Box>
              </Grid>
            </Grid>
          )}

          <Grid container>
            <Grid item xs={12} md={7}>
              <Grid container>
                <Grid item xs={12} style={{ flexGrow: 1 }}>
                  <Field
                    key="phoneNumber"
                    id="phoneNumber"
                    name="phoneNumber"
                    fieldName="phoneNumber"
                    component={returnField('phonenumber')}
                    label={t('views.confirmReservation.phoneNumber')}
                    type="text"
                    placeholder={phoneNumberExample}
                    fullWidth
                    validate={[required]}
                    required
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          <Grid container>
            <Grid item xs={12} md={7}>
              <Field
                key="email"
                id="email"
                name="email"
                component={returnField('text')}
                label={t('views.confirmReservation.email')}
                type="text"
                fullWidth
                validate={[required]}
                required
                autoComplete="email"
              />
            </Grid>
          </Grid>
          <Grid container>
            <Grid item xs={12}>
              <Field
                name="preferredChannelOfCommunication"
                component={returnField('radiogroup')}
                defaultValue="EMAIL"
                required
                validate={[required]}
                label={t('views.confirmReservation.notificationSelection')}
                values={[
                  {
                    value: 'EMAIL',
                    label: t('views.confirmReservation.byEmail'),
                  },
                  {
                    value: 'SMS',
                    label: t('views.confirmReservation.bySMS'),
                  },
                ]}
              />
            </Grid>
          </Grid>
          <Grid container>
            <Grid item xs={12} md={6}>
              <Button
                disableRipple
                type="submit"
                color="secondary"
                variant="contained"
                className={classes.button}
                disabled={submitting}>
                {t('views.confirmReservation.confirm')}
              </Button>
            </Grid>
          </Grid>
        </form>
      </Grid>
    </Grid>
  );
};

const mapStateToProps = state => {
  return {
    selectedSubService: {
      headline: R.path(['availability', 'subService'], state),
      address: R.path(['availability', 'storeAddress'], state),
      person: R.path(['availability', 'selectedOpticianName'], state),
      price: R.path(['availability', 'price'], state),
      startTime: R.path(['availability', 'startTime'], state),
      endTime: R.path(['availability', 'endTime'], state),
      timeSlot: R.path(['availability', 'timeSlot'], state),
      storeName: R.path(['availability', 'storeName'], state),
    },
    subServiceUrlFragment: R.path(['availability', 'subServiceUrlFragment'], state),
    brandSettings: R.path(['contentful', 'layout'], state),
    localeLong: R.path(['contentful', 'localeLong'], state),
  };
};

const mapDispatchToProps = (dispatch, ownProps) => ({
  createReservation: values =>
    dispatch(createReservation(values, R.path(['history'], ownProps), R.path(['lang'], ownProps), siteId)),
});

const FormDecoratedComponent = reduxForm({
  form: 'selectService',
  validate,
  enableReinitialize: true,
  keepDirtyOnReinitialize: true,
  updateUnregisteredFields: true,
})(ConfirmReservation);

ConfirmReservation.propTypes = {
  brandSettings: PropTypes.object,
  classes: PropTypes.object,
  createReservation: PropTypes.func,
  handleSubmit: PropTypes.func,
  lang: PropTypes.string,
  localeLong: PropTypes.string,
  selectedSubService: PropTypes.object,
  submitting: PropTypes.bool,
  subServiceUrlFragment: PropTypes.string,
  t: PropTypes.func,
};
ConfirmReservation.defaultProps = {
  brandSettings: {},
  classes: {},
  createReservation: null,
  handleSubmit: null,
  lang: '',
  localeLong: '',
  selectedSubService: {},
  submitting: false,
  subServiceUrlFragment: '',
  t: null,
};

export default withStyles(inlineStyles)(
  withTranslation('translations')(connect(mapStateToProps, mapDispatchToProps)(FormDecoratedComponent)),
);
